import { ScanEye, FileText, Mail, MessageSquareText, Star, History, Briefcase, StickyNote, ShieldCheck } from "lucide-react";

import Overview from "../overview";
import Interview from "../interview";
import Feedback from "../feedback";
import Activity from "../activity";
import Resume from "../resume";
import JobFit from "../job-fit";
import Notes from "../notes";
import CandidateEmail from "../email";
import Compliance from "../compliance";
import { PERMISSIONS } from "@/permissions/constant";
import { MODULES } from "@/permissions/modules";
import { EMAIL_ENABLED } from "@/config/features";

// `module` is optional. A tab that names one disappears completely when the
// tenant is not entitled — no button, and no component mounted behind it.
export const TABS = [
  {
    id: "resume_confirmed_data",
    label: "Candidate Details",
    icon: FileText,
    component: Resume,
    permissions: [
      PERMISSIONS.CANDIDATE_VIEW,
      PERMISSIONS.CANDIDATE_CREATE,
      PERMISSIONS.CANDIDATE_UPDATE,
    ]
  },
  // Second, between Candidate Details and AI Overview, where a recruiter looks
  // first. Notes are not a module — no
  // entitlement, no kill switch — so this entry names none.
  {
    id: "candidate_notes",
    label: "Notes",
    icon: StickyNote,
    component: Notes,
    permissions: [
      PERMISSIONS.NOTE_VIEW,
    ]
  },
  {
    id: "match_metadata",
    label: "AI Overview",
    icon: ScanEye,
    component: Overview,
    permissions: [
      PERMISSIONS.CANDIDATE_VIEW,
      PERMISSIONS.CANDIDATE_CREATE,
      PERMISSIONS.CANDIDATE_UPDATE,
    ]
  },
  {
    id: "job_fit",
    label: "Suitable Jobs",
    icon: Briefcase,
    component: JobFit,
    module: MODULES.AI_CANDIDATE_JOB_FIT,
    permissions: [
      PERMISSIONS.CANDIDATE_VIEW,
    ]
  },
  {
    id: "interview_questions",
    label: "Interview Question Generator",
    icon: MessageSquareText,
    component: Interview,
    module: MODULES.AI_INTERVIEW_QUESTIONS,
    permissions: [
      PERMISSIONS.INTERVIEW_MANAGE,
    ]
  },
  {
    id: "candidate_feedback",
    label: "Feedback",
    icon: Star,
    component: Feedback,
    permissions: [
      PERMISSIONS.INTERVIEW_MANAGE,
    ]
  },
  // Connectivity Phase F. Compliance progress where the recruiter already is,
  // rather than on the compliance dashboard behind a different navigation
  // path. After Feedback, per the plan.
  //
  // `module` makes the whole tab vanish for an unentitled tenant — no button,
  // and no component mounted behind it, so the tab's fetch never fires. A
  // guard inside the component would render nothing and still request.
  //
  // `candidate.view_compliance`, NOT `compliance.view`: that one gates the
  // compliance dashboard, an officer's queue across the tenant. This gates one
  // candidate's own record, and holding either implies nothing about the other.
  {
    id: "compliance",
    label: "Compliance",
    icon: ShieldCheck,
    component: Compliance,
    module: MODULES.COMPLIANCE,
    permissions: [
      PERMISSIONS.CANDIDATE_VIEW_COMPLIANCE,
    ]
  },
  // After Feedback, with the late/action tabs. The Communication Center at
  // /admin/communications stays where it is and keeps WhatsApp and Telegram —
  // this tab is the email half, scoped to the application on screen.
  //
  // Two gates, and both are needed. `EMAIL_ENABLED` is the fleet-wide kill
  // switch, and its promise is that when it is false EVERY email affordance is
  // hidden while the backend refuses to send — a tab left standing would be the
  // one that breaks it, offering a composer whose every send 400s. The spread
  // is the same shape `communications/index.jsx` uses for its Email channel
  // tab. `module` is the per-tenant entitlement, which is the other question.
  ...(EMAIL_ENABLED
    ? [
        {
          id: "outreach_email",
          label: "Email",
          icon: Mail,
          component: CandidateEmail,
          module: MODULES.OUTREACH_EMAIL,
          permissions: [
            PERMISSIONS.OUTREACH_VIEW,
            PERMISSIONS.OUTREACH_SEND,
          ]
        },
      ]
    : []),
  {
    id: "activity_logs",
    label: "Activity",
    icon: History,
    component: Activity,
    permissions: [
      PERMISSIONS.CANDIDATE_VIEW,
      PERMISSIONS.CANDIDATE_CREATE,
      PERMISSIONS.CANDIDATE_UPDATE,
    ]
  },
];
